import { useMemo, useState, type ReactNode } from 'react';
import { color, chaseTheme, redcapFlagTheme, redcapRuleTheme, type RedcapFlagSeverity } from '../styles/theme';
import { dateTime, num, plainDate, shortDate } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import type { RedcapExtractResult, RedcapWeekChange, RedcapWeekFlag, RedcapWeekPage } from '../api/client';
import {
  QUERIES_TEMPLATE,
  VISITS_TEMPLATE,
  statusReportTemplate,
  useDictionary,
  useRedcapSetup,
  useRedcapWeek,
  useUploadExtract,
  useUploadWeekFile,
  type WeekFileKind,
  type WeekFileResult,
} from '../api/redcap';
import { useAddItem, useMeetings } from '../api/meetings';
import { useCreateTicket } from '../api/tickets';
import { ApiError } from '../api/client';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardTitle, Chip, ColHead, ErrorNote, Field, Loading, Modal, Tile, selectStyle } from '../components/ui';
import { IconDownload } from '../components/icons';
import { whenLabel } from './MeetingBits';
import { Chases, CountsTable, History, LinkFirst } from './RedcapCompleteness';
import type { RedcapTab } from './RedcapSteps';
import { FileButton, GuideCard, MONO, Note, Pill, ProblemNote, Stat, Where, downloadText, problemOf, type Problem } from './RedcapShared';

/**
 * This week — step 2 of the REDCap procedure, one Monday's files on one page.
 *
 * Top to bottom it answers the weekly report's questions in the order the
 * team talks through them: which week this is (the picker), how much there
 * is (the headline), what to talk about (every flag, severity first, each
 * with the way to act on it), what changed since last Monday, then the
 * calendar, the data queries and the recruitment funnel when their files are
 * in, the upload itself, and the counts, chases and weeks on record as
 * details. The API decides the flags and the order (domain/redcapWeek.ts in
 * the API repository); this screen only lays them out and offers the actions.
 *
 * A record ID appears only when the API says the reader may see one
 * (`recordLevel`); otherwise the same flags come back counted, and this
 * screen shows exactly that.
 */

const SEVERITY_LABEL: Record<RedcapFlagSeverity, string> = { urgent: 'Urgent', watch: 'To watch', info: 'Info' };

/** 8 MB, the API's limit for any of the week's files. */
const FILE_MAX_BYTES = 8 * 1024 * 1024;

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function mondayOnOrBefore(iso: string): string {
  const day = new Date(`${iso}T12:00:00Z`).getUTCDay();
  return addDays(iso, -((day + 6) % 7));
}

const plural = (n: number, one: string, many = `${one}s`): string => `${num(n)} ${n === 1 ? one : many}`;

/* ─────────────────────────────────────────────────────────────── guide */

function WeekGuide() {
  return (
    <GuideCard
      storageKey="enact.redcap.guide.week"
      intro="Every Monday, one file from REDCap — the status of every form for every record — and this page lists what the team has to talk about. Two more exports, the calendar and the data queries, add visits and queries when the lab has them."
      steps={[
        {
          do: 'Build the status report once.',
          where: (
            <Where at="REDCap">
              Data Exports, Reports, and Stats → Create New Report. Add only the record ID, the event fields and every
              form’s “Complete?” status field. Save it as “ENACT weekly status”. Nothing else goes in — no answer, no
              score.
            </Where>
          ),
        },
        {
          do: 'Each Monday, export it and upload it here.',
          where: (
            <Where at="here">
              export the report as CSV (raw). Pick the Monday below, press Status export, choose the file. The same
              Monday again replaces the earlier upload. A file with any other column is refused and says which.
            </Where>
          ),
        },
        {
          do: 'Add the calendar and the queries when you have them.',
          where: (
            <Where at="REDCap">
              Calendar → export, and Data Quality → Resolve Issues → export. Upload each beside the status file; notes
              and comments columns are left out and listed.
            </Where>
          ),
        },
        {
          do: 'Read “Talk about this week” top to bottom.',
          where: (
            <Where at="here">
              urgent first, then to watch. Each line can go on a meeting’s agenda, open the record it names, or open
              the board. A chase is settled under Details once the forms are complete in REDCap.
            </Where>
          ),
        },
      ]}
    />
  );
}

/* ────────────────────────────────────────────────────────────── picker */

function WeekPicker({
  weeks,
  picked,
  today,
  onPick,
}: {
  weeks: RedcapWeekPage['weeks'];
  picked: string;
  today: string;
  onPick: (asOf: string) => void;
}) {
  const thisMonday = mondayOnOrBefore(today);
  const recent = weeks.slice(0, 8);
  const older = weeks.slice(8);
  const pickedIsOlder = older.some((w) => w.asOf === picked);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {recent.map((w) => (
        <Chip
          key={w.id}
          label={`${plainDate(w.asOf)}${mondayOnOrBefore(w.asOf) === thisMonday ? ' · this Monday' : ''}`}
          active={w.asOf === picked}
          onClick={() => onPick(w.asOf)}
          title={w.source === 'status' ? 'A status export' : 'A counts file'}
        />
      ))}
      {older.length > 0 && (
        <select
          aria-label="Older weeks"
          value={pickedIsOlder ? picked : ''}
          onChange={(e) => e.target.value && onPick(e.target.value)}
          style={{ ...selectStyle, width: 200, fontSize: 12.5, padding: '6px 28px 6px 10px' }}
        >
          <option value="">Older weeks…</option>
          {older.map((w) => (
            <option key={w.id} value={w.asOf}>
              {plainDate(w.asOf)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── headline */

function Headline({ page }: { page: RedcapWeekPage }) {
  const h = page.headline;
  const late = h.uploadAgeDays !== null && h.uploadAgeDays >= 8;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <Tile label="Urgent" value={num(h.urgent)} />
        <Tile label="To watch" value={num(h.watch)} />
        <Tile label="Forms not complete" value={num(h.incomplete)} />
        <Tile label="Chases" value={h.chasesOpen === 0 ? 'None open' : `${num(h.chasesOpen)} open${h.chasesOverdue ? ` · ${num(h.chasesOverdue)} overdue` : ''}`} />
        <Tile
          label="Visits this week"
          value={h.visitsThisWeek ? `${num(h.visitsThisWeek.happened)} kept${h.visitsThisWeek.noShow ? ` · ${num(h.visitsThisWeek.noShow)} missed` : ''}` : 'No calendar file'}
        />
        <Tile label="Visits next 7 days" value={h.upcoming ? num(h.upcoming.w1) : 'No calendar file'} />
        <Tile
          label="Data queries open"
          value={h.queriesOpen === null ? 'No queries file' : `${num(h.queriesOpen)}${h.queriesAged ? ` · ${num(h.queriesAged)} aged` : ''}`}
        />
        <Tile label="Records stalled" value={h.recordsStalled === null ? 'Needs 4 weeks' : num(h.recordsStalled)} />
      </div>
      {late && (
        <Note>
          <strong style={{ color: redcapFlagTheme.urgent.fg }}>
            This Monday’s file has not arrived — the newest week is {h.uploadAgeDays} days old.
          </strong>{' '}
          Upload the status export below.
        </Note>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── the list */

type TalkFilter = 'all' | 'urgent' | 'watch' | 'safety' | 'visits' | 'queries';

const SAFETY_KINDS = new Set(['chase_overdue', 'chase_open', 'safety_incomplete', 'record_multi_safety']);

function inFilter(f: RedcapWeekFlag, filter: TalkFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'urgent':
      return f.severity === 'urgent';
    case 'watch':
      return f.severity === 'watch';
    case 'safety':
      return SAFETY_KINDS.has(f.kind);
    case 'visits':
      return f.kind.startsWith('visit_');
    case 'queries':
      return f.kind === 'query_open_aged';
  }
}

/** The record, visit and form a flag is about, in one quiet line. */
function whereLine(f: RedcapWeekFlag): string {
  const parts: string[] = [];
  if (f.recordId) parts.push(`Record ${f.recordId}`);
  else if (f.recordIds && f.recordIds.length > 0) {
    const shown = f.recordIds.slice(0, 4).join(', ');
    parts.push(`Records ${shown}${f.recordIds.length > 4 ? ` +${f.recordIds.length - 4}` : ''}`);
  }
  if (f.eventName) parts.push(f.eventName);
  if (f.label) parts.push(f.label);
  if (f.date) parts.push(plainDate(f.date));
  return parts.join(' · ');
}

function whenText(f: RedcapWeekFlag): string {
  if (f.kind === 'upload_late') return f.ageDays !== null ? `${num(f.ageDays)} days` : '';
  if (f.dueOn) return `Due ${plainDate(f.dueOn)}`;
  if (f.ageDays !== null) return f.ageDays === 0 ? 'Today' : `${num(f.ageDays)} days`;
  return '';
}

const TALK_GRID = 'minmax(0,2.6fr) minmax(0,1fr) minmax(0,0.8fr) auto';

/**
 * "Make a ticket" for one flag. The title and details are built from the flag
 * alone — a record ID and a pointer, never a name — and the ticket remembers
 * the flag's key, so next Monday the page shows the ticket beside the flag
 * and a second click says which ticket is already open.
 *
 * No priority is sent: the API files a flag's ticket at the flag's own
 * (urgent → critical, watch → normal, info → low), and refuses a priority
 * from a role without tickets:update. It takes flags from the newest week
 * only, so an older week shows the tickets already made and no button
 * (`canFile` false); a flag it no longer recognises is its 404 sentence here.
 */
function MakeTicket({
  flag,
  slug,
  studyShort,
  asOf,
  pid,
  canFile,
}: {
  flag: RedcapWeekFlag;
  slug: string;
  studyShort: string;
  asOf: string;
  pid: number;
  canFile: boolean;
}) {
  const create = useCreateTicket();
  const qc = useQueryClient();
  const [made, setMade] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const small = { padding: '5px 11px', fontSize: 12 } as const;

  if (flag.pointers.ticketRef || made) {
    return (
      <span style={{ fontSize: 12, fontWeight: 700, color: color.blueDeep, whiteSpace: 'nowrap' }} title="A ticket is open for this">
        {flag.pointers.ticketRef ?? made}
      </span>
    );
  }
  if (open) {
    return (
      <span style={{ fontSize: 12, color: color.muted, whiteSpace: 'nowrap' }} title="A ticket for this is already open">
        {open} is open
      </span>
    );
  }
  if (!canFile) return null;
  const where = whereLine(flag);
  const refused = create.error && !(create.error instanceof ApiError && create.error.status === 409);
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
      <Button
        variant="soft"
        style={small}
        disabled={create.isPending}
        title="File a ticket for this, in your own name"
        onClick={() =>
          create.mutate(
            {
              title: `REDCap · ${studyShort} · ${flag.title}`.slice(0, 200),
              details: [
                `${studyShort}, REDCap week of ${plainDate(asOf)}.`,
                flag.detail,
                where ? `Where: ${where}.` : null,
                `Pointer: REDCap project ${pid}${flag.recordId ? `, record ${flag.recordId}` : ''}.`,
              ]
                .filter(Boolean)
                .join('\n'),
              studySlug: slug,
              source: { type: 'redcap_flag', key: flag.key },
            },
            {
              onSuccess: (r) => {
                setMade(r.ticket.ref);
                void qc.invalidateQueries({ queryKey: ['redcap-week'] });
              },
              onError: (err) => {
                const ref = err instanceof ApiError ? (err.details as { ref?: string } | undefined)?.ref : undefined;
                if (err instanceof ApiError && err.status === 409) setOpen(ref ?? 'A ticket');
              },
            },
          )
        }
      >
        {create.isPending ? 'Filing…' : 'Make a ticket'}
      </Button>
      {refused && (
        <span role="alert" style={{ fontSize: 11.5, fontWeight: 600, color: color.red, maxWidth: 260, textAlign: 'right' }}>
          {(create.error as Error).message}
        </span>
      )}
    </span>
  );
}

function TalkList({
  page,
  slug,
  pid,
  studyShort,
  canTickets,
  onAgenda,
  onOpenRecord,
  onOpenBoard,
}: {
  page: RedcapWeekPage;
  slug: string;
  pid: number;
  studyShort: string;
  /** tickets:read, which filing one needs. */
  canTickets: boolean;
  onAgenda: (flag: RedcapWeekFlag) => void;
  onOpenRecord: (recordId: string) => void;
  onOpenBoard: () => void;
}) {
  const [filter, setFilter] = useState<TalkFilter>('all');
  const flags = page.flags.filter((f) => inFilter(f, filter));
  // The API files a ticket only from the newest week (`weeks` is newest first).
  const newest = !!page.week && page.weeks[0]?.asOf === page.week.asOf;
  const counts = {
    urgent: page.flags.filter((f) => f.severity === 'urgent').length,
    watch: page.flags.filter((f) => f.severity === 'watch').length,
    safety: page.flags.filter((f) => SAFETY_KINDS.has(f.kind)).length,
    visits: page.flags.filter((f) => f.kind.startsWith('visit_')).length,
    queries: page.flags.filter((f) => f.kind === 'query_open_aged').length,
  };
  const small = { padding: '5px 11px', fontSize: 12 } as const;

  return (
    <Card pad={0} style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '16px 20px 12px' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <CardTitle style={{ marginBottom: 2 }}>Talk about this week</CardTitle>
          <Note>
            {page.flags.length === 0
              ? 'Nothing is flagged for this week.'
              : `${plural(page.flags.length, 'item')} for ${studyShort}, urgent first, oldest first within each.`}
            {canTickets && page.flags.length > 0 && !newest ? ' Tickets are made from the newest week only.' : ''}
          </Note>
        </div>
        <Chip label="Everything" active={filter === 'all'} onClick={() => setFilter('all')} />
        <Chip label={`Urgent${counts.urgent ? ` · ${counts.urgent}` : ''}`} active={filter === 'urgent'} onClick={() => setFilter('urgent')} />
        <Chip label={`To watch${counts.watch ? ` · ${counts.watch}` : ''}`} active={filter === 'watch'} onClick={() => setFilter('watch')} />
        <Chip label={`Safety${counts.safety ? ` · ${counts.safety}` : ''}`} active={filter === 'safety'} onClick={() => setFilter('safety')} />
        <Chip label={`Visits${counts.visits ? ` · ${counts.visits}` : ''}`} active={filter === 'visits'} onClick={() => setFilter('visits')} />
        <Chip label={`Queries${counts.queries ? ` · ${counts.queries}` : ''}`} active={filter === 'queries'} onClick={() => setFilter('queries')} />
      </div>
      <div className="x-scroll">
        <div style={{ minWidth: 820 }}>
          <div style={{ display: 'grid', gridTemplateColumns: TALK_GRID, gap: 12, padding: '8px 20px', borderTop: `1px solid ${color.line}`, borderBottom: `1px solid ${color.line}` }}>
            <ColHead>What</ColHead>
            <ColHead>Owner</ColHead>
            <ColHead>Since · due</ColHead>
            <span />
          </div>
          {flags.length === 0 && (
            <p style={{ padding: '24px 20px', fontSize: 14, color: color.muted, margin: 0 }}>
              {page.flags.length === 0 ? 'Nothing to talk about this week.' : 'Nothing in this view.'}
            </p>
          )}
          {flags.map((f) => {
            const where = whereLine(f);
            return (
              <div
                key={f.key + (f.recordId ?? '') + (f.date ?? '')}
                className="hov-row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: TALK_GRID,
                  gap: 12,
                  alignItems: 'center',
                  padding: '11px 20px',
                  borderBottom: `1px solid ${color.line}`,
                  fontSize: 13.5,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Pill tone={redcapFlagTheme[f.severity]}>{SEVERITY_LABEL[f.severity]}</Pill>
                    <span style={{ fontWeight: 700, color: color.navy }}>{f.title}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: color.muted, marginTop: 3 }}>{f.detail}</div>
                  {where && (
                    <div style={{ fontSize: 12, color: color.ink, fontFamily: MONO, marginTop: 3 }}>{where}</div>
                  )}
                </div>
                <span style={{ fontSize: 12.5, color: f.ownerName ? color.ink : color.muted }}>{f.ownerName ?? '—'}</span>
                <span style={{ fontSize: 12.5, color: f.severity === 'urgent' ? redcapFlagTheme.urgent.fg : color.muted, fontWeight: f.severity === 'urgent' ? 700 : 500 }}>
                  {whenText(f)}
                </span>
                <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
                  <MakeTicket
                    flag={f}
                    slug={slug}
                    studyShort={studyShort}
                    asOf={page.week?.asOf ?? page.today}
                    pid={pid}
                    canFile={canTickets && newest}
                  />
                  <Button variant="soft" style={small} onClick={() => onAgenda(f)} title="Put this line on a meeting’s agenda">
                    Put on the agenda
                  </Button>
                  {page.recordLevel && f.recordId && (
                    <Button variant="ghost" style={small} onClick={() => onOpenRecord(f.recordId!)} title="Open this record under Record import">
                      Open record
                    </Button>
                  )}
                  <Button variant="ghost" style={small} onClick={onOpenBoard} title="Open the study’s participant board">
                    Board
                  </Button>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

/* ─────────────────────────────────────────────────────── agenda picker */

/**
 * Which meeting a line goes to: one still being prepared. The agenda line
 * carries the flag's title, its record ID and visit — the workflow's
 * "record identifier and a pointer" — never a name.
 */
function AgendaPicker({
  flag,
  studyShort,
  asOf,
  onClose,
  onOpenMeeting,
  onGoMeetings,
}: {
  flag: RedcapWeekFlag;
  studyShort: string;
  asOf: string;
  onClose: () => void;
  onOpenMeeting: (id: string) => void;
  onGoMeetings: () => void;
}) {
  const meetings = useMeetings({ phase: 'preparing', pageSize: 20 });
  const add = useAddItem();
  const [picked, setPicked] = useState<string>('');
  const list = meetings.data?.rows ?? [];
  const where = whereLine(flag);
  const body = `${flag.title}${where ? ` — ${where}` : ''} (${studyShort}, REDCap week of ${plainDate(asOf)})`;

  return (
    <Modal label="Put on the agenda" onClose={onClose} header={<CardTitle>Put on the agenda</CardTitle>} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ background: color.tint, borderRadius: 14, padding: '10px 14px', fontSize: 13, color: color.ink }}>{body}</div>
        {meetings.isLoading && <Loading />}
        {meetings.error && <ErrorNote message={(meetings.error as Error).message} />}
        {meetings.data && list.length === 0 && (
          <>
            <Note>No meeting is being prepared. Start one under Meetings, then put this on its agenda.</Note>
            <div>
              <Button variant="soft" onClick={onGoMeetings}>
                Go to Meetings
              </Button>
            </div>
          </>
        )}
        {list.length > 0 && (
          <>
            <Field label="Meeting">
              <select value={picked} onChange={(e) => setPicked(e.target.value)} style={selectStyle}>
                <option value="">Choose a meeting…</option>
                {list.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title} · {whenLabel(m.scheduledAt)}
                  </option>
                ))}
              </select>
            </Field>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                disabled={!picked || add.isPending}
                onClick={() =>
                  add.mutate(
                    { id: picked, kind: 'agenda', body, source: 'manual' },
                    {
                      onSuccess: () => {
                        onClose();
                        onOpenMeeting(picked);
                      },
                    },
                  )
                }
              >
                {add.isPending ? 'Adding…' : 'Add to the agenda'}
              </Button>
            </div>
          </>
        )}
        {add.error && <ProblemNote problem={problemOf(add.error)} />}
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────── changes */

const CHANGE_TONE: Record<RedcapWeekChange['kind'], { label: string; tone: { bg: string; fg: string } }> = {
  cell_cleared: { label: 'Cleared', tone: chaseTheme.done },
  cell_newly_incomplete: { label: 'Newly behind', tone: redcapRuleTheme.held },
  cell_moved: { label: 'Moved', tone: redcapRuleTheme.none },
  cell_added: { label: 'New', tone: chaseTheme.open },
  cell_removed: { label: 'Gone', tone: redcapRuleTheme.none },
  chase_opened: { label: 'Chase opened', tone: redcapRuleTheme.chase },
  chase_resolved: { label: 'Chase cleared', tone: chaseTheme.done },
  chase_done: { label: 'Chase done', tone: chaseTheme.done },
  dictionary_changed: { label: 'Dictionary', tone: chaseTheme.open },
  record_progress: { label: 'Progress', tone: chaseTheme.done },
  visits_delta: { label: 'Calendar', tone: redcapRuleTheme.none },
  queries_delta: { label: 'Queries', tone: redcapRuleTheme.none },
};

const SHOWN_CHANGES = 12;

function ChangeLog({ page }: { page: RedcapWeekPage }) {
  const [all, setAll] = useState(false);
  const rows = all ? page.changes : page.changes.slice(0, SHOWN_CHANGES);
  return (
    <Card pad={0}>
      <div style={{ padding: '16px 20px 10px' }}>
        <CardTitle style={{ marginBottom: 2 }}>What changed</CardTitle>
        <Note>
          {page.previous
            ? `Since the week of ${plainDate(page.previous.asOf)}.`
            : 'The first week on record — nothing to compare with yet.'}
        </Note>
      </div>
      {page.changes.length === 0 && page.previous && (
        <p style={{ padding: '4px 20px 18px', margin: 0, fontSize: 13.5, color: color.muted }}>Nothing moved since last week.</p>
      )}
      {rows.map((c, i) => {
        const t = CHANGE_TONE[c.kind];
        return (
          <div
            key={`${c.kind}|${c.eventName ?? ''}|${c.instrument ?? ''}|${i}`}
            style={{ display: 'grid', gridTemplateColumns: '112px 1fr', gap: 10, alignItems: 'start', padding: '9px 20px', borderTop: `1px solid ${color.line}` }}
          >
            <span>
              <Pill tone={t.tone}>{t.label}</Pill>
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: color.ink }}>{c.title}</span>
              <span style={{ display: 'block', fontSize: 12.5, color: color.muted, marginTop: 2 }}>{c.detail}</span>
            </span>
          </div>
        );
      })}
      {page.changes.length > SHOWN_CHANGES && (
        <div style={{ padding: '10px 20px 14px', borderTop: `1px solid ${color.line}` }}>
          <button
            onClick={() => setAll((v) => !v)}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: color.blue }}
          >
            {all ? 'Show fewer' : `Show ${num(page.changes.length - SHOWN_CHANGES)} more`}
          </button>
        </div>
      )}
    </Card>
  );
}

/* ───────────────────────────────────────────────────────────── panels */

function MissingFile({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardTitle style={{ marginBottom: 6 }}>{title}</CardTitle>
      <Note>{children}</Note>
    </Card>
  );
}

const VISIT_WORD: Record<NonNullable<RedcapWeekPage['visits']>['rows'][number]['status'], string> = {
  due: 'Due',
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

const WHEN_WORD: Record<NonNullable<RedcapWeekPage['visits']>['rows'][number]['when'], string> = {
  past: 'Earlier',
  thisWeek: 'This week',
  w1: 'Next 7 days',
  w2: 'In 2 weeks',
  w4: 'In 4 weeks',
  later: 'Later',
};

function VisitsPanel({ page }: { page: RedcapWeekPage }) {
  const v = page.visits;
  const h = page.headline;
  if (!v) {
    return (
      <MissingFile title="Visits">
        No calendar file for this week. In REDCap, export the Calendar for the study and upload it beside the status
        file: this panel then shows what happened this week and what is coming in 1, 2 and 4 weeks.
      </MissingFile>
    );
  }
  const rows = v.rows.filter((r) => r.when !== 'past' && r.when !== 'later').sort((a, b) => a.date.localeCompare(b.date));
  const grid = 'minmax(0,1fr) minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)';
  return (
    <Card pad={0}>
      <div style={{ padding: '16px 20px 12px' }}>
        <CardTitle style={{ marginBottom: 8 }}>Visits</CardTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
          <Stat label="Kept this week" value={num(h.visitsThisWeek?.happened ?? 0)} />
          <Stat label="Missed" value={num(h.visitsThisWeek?.noShow ?? 0)} />
          <Stat label="Cancelled" value={num(h.visitsThisWeek?.cancelled ?? 0)} />
          <Stat label="Next 7 days" value={num(h.upcoming?.w1 ?? 0)} />
          <Stat label="Next 14 days" value={num(h.upcoming?.w2 ?? 0)} />
          <Stat label="Next 28 days" value={num(h.upcoming?.w4 ?? 0)} />
        </div>
      </div>
      {rows.length === 0 ? (
        <p style={{ padding: '4px 20px 18px', margin: 0, fontSize: 13.5, color: color.muted }}>Nothing on the calendar for this week or the next four.</p>
      ) : (
        <div className="x-scroll">
          <div style={{ minWidth: 640 }}>
            <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, padding: '0 20px 8px' }}>
              <ColHead>Date</ColHead>
              <ColHead>Visit</ColHead>
              <ColHead>Record</ColHead>
              <ColHead>Status</ColHead>
              <ColHead>When</ColHead>
            </div>
            {rows.map((r, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, padding: '9px 20px', borderTop: `1px solid ${color.line}`, fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: color.navy }}>{plainDate(r.date)}</span>
                <span style={{ color: color.ink }}>{r.eventName || '—'}</span>
                <span style={{ fontFamily: MONO, color: r.recordId ? color.ink : color.muted }}>{r.recordId ?? '—'}</span>
                <span style={{ color: r.status === 'no_show' ? redcapFlagTheme.urgent.fg : color.ink, fontWeight: r.status === 'no_show' ? 700 : 500 }}>
                  {VISIT_WORD[r.status]}
                </span>
                <span style={{ color: color.muted }}>{WHEN_WORD[r.when]}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function QueriesPanel({ page }: { page: RedcapWeekPage }) {
  const q = page.queries;
  if (!q) {
    return (
      <MissingFile title="Data queries">
        No queries file for this week. In REDCap, open Data Quality → Resolve Issues and export the list; uploaded beside
        the status file, this panel shows which queries are open, how old they are, and who they are with.
      </MissingFile>
    );
  }
  const open = q.rows.filter((r) => r.status === 'open' || r.status === 'deverified').sort((a, b) => (b.ageDays ?? 0) - (a.ageDays ?? 0));
  const grid = 'minmax(0,1fr) minmax(0,1.3fr) minmax(0,1.2fr) minmax(0,0.8fr) minmax(0,1fr)';
  return (
    <Card pad={0}>
      <div style={{ padding: '16px 20px 12px' }}>
        <CardTitle style={{ marginBottom: 8 }}>Data queries</CardTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {q.byInstrument.slice(0, 6).map((g) => (
            <Stat key={g.instrument ?? 'other'} label={g.label} value={`${num(g.open)} open${g.aged ? ` · ${num(g.aged)} aged` : ''}`} />
          ))}
        </div>
      </div>
      {open.length === 0 ? (
        <p style={{ padding: '4px 20px 18px', margin: 0, fontSize: 13.5, color: color.muted }}>No data query is open.</p>
      ) : (
        <div className="x-scroll">
          <div style={{ minWidth: 680 }}>
            <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, padding: '0 20px 8px' }}>
              <ColHead>Record</ColHead>
              <ColHead>Field</ColHead>
              <ColHead>Rule</ColHead>
              <ColHead>Open</ColHead>
              <ColHead>With</ColHead>
            </div>
            {open.map((r, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, padding: '9px 20px', borderTop: `1px solid ${color.line}`, fontSize: 13 }}>
                <span style={{ fontFamily: MONO, color: r.recordId ? color.ink : color.muted }}>
                  {r.recordId ?? '—'}
                  {r.eventName ? <span style={{ display: 'block', fontSize: 11.5, color: color.muted, fontFamily: 'inherit' }}>{r.eventName}</span> : null}
                </span>
                <span style={{ color: color.ink }}>
                  {r.label ?? r.field ?? '—'}
                  {r.field && r.label ? <span style={{ display: 'block', fontSize: 11.5, color: color.muted, fontFamily: MONO }}>{r.field}</span> : null}
                </span>
                <span style={{ color: color.muted }}>{r.category ?? '—'}</span>
                <span style={{ color: (r.ageDays ?? 0) > 21 ? redcapFlagTheme.urgent.fg : (r.ageDays ?? 0) > 7 ? redcapFlagTheme.watch.fg : color.ink, fontWeight: (r.ageDays ?? 0) > 7 ? 700 : 500 }}>
                  {r.ageDays === null ? '—' : `${num(r.ageDays)} days`}
                </span>
                <span style={{ color: r.assigned ? color.ink : color.muted }}>{r.assigned ?? 'Nobody'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function FunnelPanel({ page }: { page: RedcapWeekPage }) {
  if (page.funnel.length === 0) return null;
  const grid = 'minmax(0,2fr) minmax(0,1fr) minmax(0,1fr)';
  return (
    <Card pad={0}>
      <div style={{ padding: '16px 20px 10px' }}>
        <CardTitle style={{ marginBottom: 2 }}>Recruitment funnel</CardTitle>
        <Note>Records with each recruitment form complete at any visit, in the dictionary’s order; the change is since last week’s file.</Note>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, padding: '0 20px 8px' }}>
        <ColHead>Form</ColHead>
        <ColHead>Records complete</ColHead>
        <ColHead>This week</ColHead>
      </div>
      {page.funnel.map((f) => (
        <div key={f.instrument} style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, padding: '9px 20px', borderTop: `1px solid ${color.line}`, fontSize: 13 }}>
          <span style={{ fontWeight: 700, color: color.navy }}>{f.label}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{num(f.cumulative)}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', color: f.thisWeek && f.thisWeek > 0 ? color.green : color.muted }}>
            {f.thisWeek === null ? '—' : f.thisWeek > 0 ? `+${num(f.thisWeek)}` : num(f.thisWeek)}
          </span>
        </div>
      ))}
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────── uploads */

function chaseSummary(c: RedcapExtractResult['chases']): string {
  const parts: string[] = [];
  if (c.opened) parts.push(`opened ${plural(c.opened, 'chase')}`);
  if (c.resolved) parts.push(`cleared ${c.resolved}`);
  if (c.updated) parts.push(`updated ${c.updated}`);
  return parts.length ? `${parts.join(', ').replace(/^./, (s) => s.toUpperCase())}.` : 'No chase changed.';
}

function WeekUploadCard({
  slug,
  name,
  pid,
  today,
  weeks,
  canWrite,
  recordIdField,
  forms,
  onUploaded,
}: {
  slug: string;
  name: string;
  pid: number;
  today: string;
  weeks: RedcapWeekPage['weeks'];
  canWrite: boolean;
  recordIdField: string | null;
  forms: readonly string[];
  onUploaded: (asOf: string) => void;
}) {
  const thisMonday = mondayOnOrBefore(today);
  const mondays = useMemo(() => {
    const set = new Set<string>();
    for (let i = 0; i < 8; i++) set.add(addDays(thisMonday, -7 * i));
    for (const w of weeks) set.add(w.asOf);
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [thisMonday, weeks]);
  const [monday, setMonday] = useState(thisMonday);
  const hasStatus = weeks.some((w) => w.asOf === monday);

  const uploadStatus = useUploadExtract();
  const uploadFile = useUploadWeekFile();
  const [statusResult, setStatusResult] = useState<RedcapExtractResult | null>(null);
  const [fileResult, setFileResult] = useState<WeekFileResult | null>(null);
  const [problem, setProblem] = useState<{ kind: 'status' | WeekFileKind; problem: Problem } | null>(null);

  async function onStatus(file: File) {
    setStatusResult(null);
    setFileResult(null);
    setProblem(null);
    if (file.size > FILE_MAX_BYTES) {
      setProblem({ kind: 'status', problem: { message: 'That file is larger than a week’s export can be (8 MB).' } });
      return;
    }
    try {
      const r = await uploadStatus.mutateAsync({ slug, csv: await file.text(), asOf: monday });
      setStatusResult(r);
      onUploaded(r.asOf);
    } catch (err) {
      setProblem({ kind: 'status', problem: problemOf(err) });
    }
  }

  async function onFile(kind: WeekFileKind, file: File) {
    setStatusResult(null);
    setFileResult(null);
    setProblem(null);
    if (file.size > FILE_MAX_BYTES) {
      setProblem({ kind, problem: { message: 'That file is larger than a week’s export can be (8 MB).' } });
      return;
    }
    try {
      const r = await uploadFile.mutateAsync({ slug, asOf: monday, kind, csv: await file.text() });
      setFileResult(r);
      onUploaded(r.asOf);
    } catch (err) {
      setProblem({ kind, problem: problemOf(err) });
    }
  }

  const busy = uploadStatus.isPending || uploadFile.isPending;
  const ghost = { padding: '7px 13px', fontSize: 12.5 } as const;

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <CardTitle style={{ marginBottom: 6 }}>
            Upload {name}’s files for the week of {plainDate(monday)}
          </CardTitle>
          <Note>
            REDCap project {num(pid)}. The status export is the week; the calendar and the queries exports sit beside it
            once it is in. The same Monday again replaces what was uploaded. Nothing in these files may be an answer —
            a column that is one refuses the whole file, by name.
          </Note>
        </div>
        <Field label="Monday" style={{ width: 200 }}>
          <select value={monday} onChange={(e) => setMonday(e.target.value)} style={selectStyle} disabled={busy}>
            {mondays.map((m) => (
              <option key={m} value={m}>
                {plainDate(m)}
                {m === thisMonday ? ' · this Monday' : ''}
                {weeks.some((w) => w.asOf === m) ? ' · on record' : ''}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {canWrite ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 14 }}>
          <FileButton label={uploadStatus.isPending ? 'Uploading…' : 'Status export'} disabled={busy} onFile={onStatus} />
          <FileButton
            label="Calendar export"
            variant="soft"
            disabled={busy || !hasStatus}
            onFile={(f) => void onFile('visits', f)}
          />
          <FileButton
            label="Queries export"
            variant="soft"
            disabled={busy || !hasStatus}
            onFile={(f) => void onFile('queries', f)}
          />
          {!hasStatus && (
            <span style={{ fontSize: 12.5, color: color.muted }}>The calendar and queries files follow the status export for this Monday.</span>
          )}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" style={ghost} onClick={() => downloadText('redcap-status-report-columns.csv', statusReportTemplate(recordIdField, forms))}>
            <IconDownload /> Report columns
          </Button>
          <Button variant="ghost" style={ghost} onClick={() => downloadText('redcap-calendar-columns.csv', VISITS_TEMPLATE)}>
            <IconDownload /> Calendar columns
          </Button>
          <Button variant="ghost" style={ghost} onClick={() => downloadText('redcap-queries-columns.csv', QUERIES_TEMPLATE)}>
            <IconDownload /> Queries columns
          </Button>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <Note>Uploading needs the REDCap permission.</Note>
        </div>
      )}

      {statusResult && (
        <div role="status" style={{ marginTop: 14, background: '#cfe9d8', color: '#14563c', borderRadius: 14, padding: '12px 16px', fontSize: 13.5, fontWeight: 600 }}>
          Saved the week of {plainDate(statusResult.asOf)} for {name} (PID {num(statusResult.pid)}):{' '}
          {statusResult.source === 'status'
            ? `${plural(statusResult.records ?? 0, 'record')}, ${plural(statusResult.forms ?? 0, 'form')}, ${plural(statusResult.cells, 'count')}`
            : `${plural(statusResult.cells, 'count')} from the counts file`}
          {statusResult.replaced ? ', replacing the earlier upload of that week' : ''}.{' '}
          {statusResult.latest ? chaseSummary(statusResult.chases) : 'It is older than the newest week on record, so it changes no chase on its own.'}
        </div>
      )}
      {fileResult && (
        <div role="status" style={{ marginTop: 14, background: '#cfe9d8', color: '#14563c', borderRadius: 14, padding: '12px 16px', fontSize: 13.5, fontWeight: 600 }}>
          Saved the {fileResult.kind === 'visits' ? 'calendar' : 'queries'} export for the week of {plainDate(fileResult.asOf)}:{' '}
          {plural(fileResult.rows, 'row')}
          {fileResult.replaced ? ', replacing the earlier one' : ''}.
          {fileResult.droppedColumns.length > 0 && (
            <>
              {' '}Left out: {fileResult.droppedColumns.map((c) => <code key={c} style={{ fontFamily: MONO, marginRight: 6 }}>{c}</code>)}
            </>
          )}
        </div>
      )}
      {problem && (
        <div style={{ marginTop: 14 }}>
          <ProblemNote problem={problem.problem} />
        </div>
      )}
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────── details */

function Details({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: color.blue }}
      >
        {open ? 'Hide' : 'Show'} the details: counts, chases and weeks on record
      </button>
      {open && children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── page */

export function RedcapWeek({
  slug,
  name,
  onGo,
  onOpenBoard,
  onOpenMeeting,
  onGoMeetings,
  onOpenRecord,
}: {
  slug: string;
  name: string;
  onGo: (tab: RedcapTab) => void;
  onOpenBoard: () => void;
  onOpenMeeting: (id: string) => void;
  onGoMeetings: () => void;
  onOpenRecord: (recordId: string) => void;
}) {
  const { can } = useAuth();
  const canWrite = can('redcap:import');
  const [asOf, setAsOf] = useState<string | null>(null);
  const week = useRedcapWeek(slug, asOf);
  const dict = useDictionary(slug);
  const setup = useRedcapSetup();
  const [agendaFor, setAgendaFor] = useState<RedcapWeekFlag | null>(null);

  if (week.isLoading || dict.isLoading) return <Loading />;
  if (week.error) return <ErrorNote message={(week.error as Error).message} />;
  if (dict.error) return <ErrorNote message={(dict.error as Error).message} />;
  const page = week.data!;
  const project = dict.data?.project ?? null;

  if (!project) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <LinkFirst name={name} canWrite={canWrite} onGo={onGo} />
        <WeekGuide />
      </div>
    );
  }

  const forms = setup.data?.summary.forms.map((f) => f.name) ?? [];
  const upload = (
    <WeekUploadCard
      slug={slug}
      name={name}
      pid={project.pid}
      today={page.today}
      weeks={page.weeks}
      canWrite={canWrite}
      recordIdField={project.recordIdField}
      forms={forms}
      onUploaded={setAsOf}
    />
  );

  if (!page.week) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <WeekGuide />
        <Card>
          <CardTitle style={{ marginBottom: 6 }}>No week on record for {name} yet</CardTitle>
          <Note>
            Upload this Monday’s status export below and this page fills in: the headline, what to talk about, and what
            changes from one Monday to the next.
          </Note>
        </Card>
        {upload}
      </div>
    );
  }

  const w = page.week;
  const stale = (page.headline.uploadAgeDays ?? 0) >= 8 || mondayOnOrBefore(page.today) !== mondayOnOrBefore(w.asOf);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <WeekGuide />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <WeekPicker weeks={page.weeks} picked={w.asOf} today={page.today} onPick={setAsOf} />
        </div>
        <Button variant="ghost" onClick={() => onGo('setup')} style={{ padding: '7px 14px', fontSize: 12 }}>
          Change the project in Project setup
        </Button>
      </div>

      <Note>
        Week of {plainDate(w.start)} to {plainDate(w.end)}, from the {w.source === 'status' ? 'status export' : 'counts file'} of {plainDate(w.asOf)}
        {w.recordCount !== null ? ` · ${plural(w.recordCount, 'record')}` : ''} · dictionary {w.dictionaryVersion} · uploaded {dateTime(w.uploadedAt)}
        {w.uploadedByName ? ` by ${w.uploadedByName}` : ''}
        {w.replacedAt ? ` · re-uploaded ${dateTime(w.replacedAt)}` : ''}
        {w.files.visits ? ` · calendar ${shortDate(w.files.visits.uploadedAt)}` : ''}
        {w.files.queries ? ` · queries ${shortDate(w.files.queries.uploadedAt)}` : ''}.
      </Note>

      <Headline page={page} />

      {stale && upload}

      <TalkList
        page={page}
        slug={slug}
        pid={project.pid}
        studyShort={name}
        canTickets={can('tickets:read')}
        onAgenda={setAgendaFor}
        onOpenRecord={onOpenRecord}
        onOpenBoard={onOpenBoard}
      />

      <ChangeLog page={page} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 14 }}>
        <VisitsPanel page={page} />
        <QueriesPanel page={page} />
      </div>

      <FunnelPanel page={page} />

      {!stale && upload}

      <Details>
        <CountsTable slug={slug} asOf={w.asOf} previousAsOf={page.previous?.asOf ?? null} cells={page.cells} canWrite={canWrite} />
        <Chases chases={page.chases} slug={slug} canWrite={canWrite} />
        <History weeks={history(page)} picked={w.asOf} onPick={setAsOf} />
      </Details>

      {agendaFor && (
        <AgendaPicker
          flag={agendaFor}
          studyShort={name}
          asOf={w.asOf}
          onClose={() => setAgendaFor(null)}
          onOpenMeeting={onOpenMeeting}
          onGoMeetings={onGoMeetings}
        />
      )}
    </div>
  );
}

/**
 * The weeks table wants each week's totals; the page carries them only for
 * the week shown, so the list shows the picked week in full and the others by
 * date and file — enough to pick one.
 */
function history(page: RedcapWeekPage): Parameters<typeof History>[0]['weeks'] {
  const w = page.week!;
  return page.weeks.map((row) =>
    row.asOf === w.asOf
      ? w
      : {
          id: row.id,
          asOf: row.asOf,
          dictionaryVersion: '—',
          source: row.source,
          pid: w.pid,
          cells: 0,
          recordCount: null,
          expected: 0,
          complete: 0,
          incomplete: 0,
          uploadedAt: w.uploadedAt,
          uploadedByName: null,
          replacedAt: null,
        },
  );
}
